import { useState } from "react";
import { SAMPLES, CATEGORIES } from "@/data/portfolioSamples";
import PortfolioLightbox from "@/components/site/PortfolioLightbox";

/**
 * Samples showcase used on the RLAH agent page. Reuses the shared
 * portfolio data so uploads only need to be updated in one place.
 * Each image renders at its natural aspect ratio (no forced 4:3 crop),
 * so wide multi-level plans and tall aerial renders both fit nicely.
 */
export default function PlanShowcase() {
  const [active, setActive] = useState("bw");
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const category = CATEGORIES.find((c) => c.id === active);
  const items = SAMPLES[active];

  // Build a flat gallery of image {src, alt} pairs for the current tab.
  // For redraw, expand each item into a Before + After pair.
  const galleryItems = active === "redraw"
    ? items.flatMap((it) => [
        { src: it.before, alt: `${it.label} — Before` },
        { src: it.after, alt: `${it.label} — After` },
      ])
    : items.map((it) => ({ src: it.src, alt: it.label }));

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });

  return (
    <section
      id="samples"
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF7F2] relative overflow-hidden"
      data-testid="samples-section"
    >
      <div className="blob blob-butter w-[320px] h-[320px] -top-10 left-1/3" />
      <div className="relative mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 items-end">
          <div className="lg:col-span-7">
            <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
              Selected work — for RLAH agents
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-[1.05]">
              Every listing,<br />
              <span className="italic font-medium text-teal-700">
                measure-ready.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-600 text-lg leading-relaxed">
              A quick look at the finishes we deliver — from crisp MLS-ready
              linework to fully rendered aerial site plans.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap items-center gap-2 mb-6"
          data-testid="samples-tabs"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.id)}
              data-testid={`samples-tab-${c.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                active === c.id
                  ? c.activeBtn
                  : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Samples wall — natural aspect per image, no forced crop */}
        <div className="bg-white border border-stone-200 rounded-[2rem] p-4 sm:p-6 lg:p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <span
              className={`inline-block ${category.chip} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider`}
            >
              {category.label}
            </span>
            <span className="text-xs text-stone-500">{category.blurb}</span>
          </div>

          {active === "redraw" ? (
            <RedrawGrid items={items} onOpen={openLightbox} />
          ) : (
            <NaturalGrid items={items} category={active} onOpen={openLightbox} />
          )}
        </div>
      </div>

      <PortfolioLightbox
        open={lightbox.open}
        items={galleryItems}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox({ open: true, index: i })}
        onClose={closeLightbox}
      />
    </section>
  );
}

/**
 * Grid where each image keeps its own aspect ratio (no forced 4:3 letterbox).
 * Uses `columns` so tall/wide plans coexist without cropping.
 */
function NaturalGrid({ items, category, onOpen }) {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
      {items.map((item, idx) => (
        <button
          key={item.src}
          type="button"
          onClick={() => onOpen?.(idx)}
          data-testid={`samples-${category}-${idx + 1}`}
          className="mb-5 break-inside-avoid block w-full text-left rounded-2xl overflow-hidden border border-stone-200 bg-[#FAF7F2] hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <img
            src={item.src}
            alt={item.label}
            loading="lazy"
            draggable={false}
            className="w-full h-auto block"
          />
        </button>
      ))}
    </div>
  );
}

function RedrawGrid({ items, onOpen }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {items.map((item, idx) => (
        <div
          key={item.after}
          className="rounded-2xl overflow-hidden border border-stone-200 bg-[#FAF7F2]"
          data-testid={`samples-redraw-${idx + 1}`}
        >
          <div className="grid grid-cols-2 gap-px bg-stone-200">
            <button
              type="button"
              onClick={() => onOpen?.(idx * 2)}
              className="relative bg-white overflow-hidden group text-left"
            >
              <img
                src={item.before}
                alt="Before"
                loading="lazy"
                className="w-full h-auto block"
              />
              <span className="absolute top-3 left-3 bg-amber-700/90 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                Before
              </span>
            </button>
            <button
              type="button"
              onClick={() => onOpen?.(idx * 2 + 1)}
              className="relative bg-white overflow-hidden group text-left"
            >
              <img
                src={item.after}
                alt="After"
                loading="lazy"
                className="w-full h-auto block"
              />
              <span className="absolute top-3 left-3 bg-teal-700/90 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                After
              </span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
